import { Injectable, inject } from '@angular/core';
import { Auth, sendPasswordResetEmail } from '@angular/fire/auth';
import { Functions, httpsCallable } from '@angular/fire/functions';

export interface CreateOperatorData {
  displayName: string;
  email: string;
}

export interface CreateOperatorResult {
  success: boolean;
  uid?: string;
  error?: string;
}

export interface DeleteOperatorResult {
  success: boolean;
  error?: string;
}

interface CloudFunctionResponse {
  success: boolean;
  uid: string;
  email: string;
}

interface DeleteOperatorResponse {
  success: boolean;
  uid: string;
}

@Injectable({
  providedIn: 'root'
})
export class OperatorsService {
  private readonly auth = inject(Auth);
  private readonly functions = inject(Functions);

  async createOperator(data: CreateOperatorData): Promise<CreateOperatorResult> {
    const { displayName, email } = data;

    try {
      const createOperatorFn = httpsCallable<CreateOperatorData, CloudFunctionResponse>(
        this.functions,
        'createOperator'
      );
      const result = await createOperatorFn({ displayName, email });

      await sendPasswordResetEmail(this.auth, result.data.email);

      return { success: true, uid: result.data.uid };

    } catch (err: unknown) {
      const errorMessage = this.parseFirebaseError(err);
      return { success: false, error: errorMessage };
    }
  }

  async deleteOperator(uid: string): Promise<DeleteOperatorResult> {
    try {
      const deleteOperatorFn = httpsCallable<{ uid: string }, DeleteOperatorResponse>(
        this.functions,
        'deleteOperator'
      );
      await deleteOperatorFn({ uid });

      return { success: true };

    } catch (err: unknown) {
      return { success: false, error: this.parseDeleteError(err) };
    }
  }

  private parseDeleteError(err: unknown): string {
    if (err && typeof err === 'object' && 'code' in err) {
      const firebaseError = err as { code: string; message: string };

      if (firebaseError.code === 'functions/not-found') {
        return 'Operator not found';
      } else if (firebaseError.code === 'functions/failed-precondition') {
        return 'User is not an operator';
      } else if (firebaseError.code === 'functions/permission-denied') {
        // Server explains why (not an admin, or operator from another company)
        return firebaseError.message || 'You do not have permission to delete this operator';
      }

      return firebaseError.message || 'Failed to delete operator';
    }

    return err instanceof Error ? err.message : 'Failed to delete operator';
  }

  private parseFirebaseError(err: unknown): string {
    // Firebase Functions errors have a 'code' and 'message' property
    if (err && typeof err === 'object' && 'code' in err) {
      const firebaseError = err as { code: string; message: string };
      
      if (firebaseError.code === 'functions/already-exists') {
        return 'This email is already registered';
      } else if (firebaseError.code === 'functions/invalid-argument') {
        return firebaseError.message || 'Invalid input data';
      } else if (firebaseError.code === 'functions/permission-denied') {
        return 'You do not have permission to create operators';
      }
      
      return firebaseError.message || 'Failed to create operator';
    }

    return err instanceof Error ? err.message : 'Failed to create operator';
  }

  isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
}
